import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import {
  PageHeader, SectionCard, EmptyState, StatCard, StickyPageHead,
} from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, FileText } from "lucide-react";
import { toast } from "sonner";
import {
  InvoiceItemsEditor, serializeInvoiceItems, makeEmptyInvoiceItem,
} from "@/components/InvoiceItemsEditor";
import DateRangeFilter, { isDateInRange } from "@/components/DateRangeFilter";
import { MOBILE_STICKY_FOOTER } from "@/lib/utils";

/**
 * Faturalar — sale + purchase invoices scoped to the active tenant.
 *
 * All monetary totals are server-computed at write time.  The list
 * screen never mutates totals; item edits open the same dialog which
 * re-POSTs the entire payload so per-item vat_rate snapshots always
 * come back from the backend.
 */
export default function Invoices() {
  const [type, setType] = useState("sale");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Tarih filtresi Satış ve Alış tabları için ORTAK tutulur; ekran
  // sekmesi değiştirildiğinde kullanıcının seçimi korunur.  Aktif filtre
  // ``invoice_date`` (kullanıcının faturaya girdiği tarih) üzerinden
  // client-side filtreler; ``created_at`` DEĞİL.
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices?type=${type}`);
    setItems(data);
  }, [type]);
  const loadRefs = async () => {
    const [c, v, j, p, vr] = await Promise.all([
      api.get("/customers"), api.get("/vendors"), api.get("/jobs"),
      api.get("/purchases"), api.get("/vat-rates?status=active"),
    ]);
    setCustomers(c.data?.items ?? c.data ?? []);
    setVendors(v.data?.items ?? v.data ?? []);
    setJobs(j.data ?? []);
    setPurchases(p.data?.items ?? p.data ?? []);
    setVatRates(vr.data ?? []);
  };
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadRefs(); }, []);

  const filteredItems = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return items ?? [];
    return (items ?? []).filter((it) =>
      isDateInRange(it.invoice_date, dateRange.from, dateRange.to));
  }, [items, dateRange]);

  const totals = useMemo(() => {
    let ex = 0, vat = 0, gt = 0;
    for (const it of filteredItems ?? []) {
      ex += Number(it.subtotal_ex_vat) || 0;
      vat += Number(it.total_vat) || 0;
      gt += Number(it.grand_total) || 0;
    }
    return { ex, vat, gt, count: (filteredItems ?? []).length };
  }, [filteredItems]);

  const onDelete = async (id) => {
    if (!window.confirm("Bu fatura silinsin mi?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Fatura silindi");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Silinemedi");
    }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <StickyPageHead>
        <PageHeader
          title="Faturalar"
          subtitle="Satış ve alış faturalarınızı buradan yönetebilirsiniz."
          testId="invoices-header"
          action={
            <Button
              className="bg-slate-900 hover:bg-slate-800 rounded-lg"
              onClick={() => { setEditing(null); setOpen(true); }}
              data-testid="new-invoice-button"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Fatura
            </Button>
          }
        />
      </StickyPageHead>

      <div className="flex flex-wrap items-center gap-2">
        <TabPill active={type === "sale"} onClick={() => setType("sale")} testId="invoice-tab-sale">
          Satış Faturaları
        </TabPill>
        <TabPill active={type === "purchase"} onClick={() => setType("purchase")} testId="invoice-tab-purchase">
          Alış Faturaları
        </TabPill>
        <div className="ml-auto">
          <DateRangeFilter
            value={dateRange}
            onChange={setDateRange}
            testId="invoices-date-range"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Fatura Sayısı" value={String(totals.count)} accent="slate" testId="invoice-stat-count" />
        <StatCard label="KDV Hariç Toplam" value={fmtTRY(totals.ex)} accent="slate" />
        <StatCard label="Toplam KDV" value={fmtTRY(totals.vat)} accent="amber" />
        <StatCard label="Genel Toplam" value={fmtTRY(totals.gt)} accent="emerald" />
      </div>

      <SectionCard title={type === "sale" ? "Satış Faturaları" : "Alış Faturaları"}>
        {(filteredItems ?? []).length === 0 ? (
          <EmptyState
            icon={<FileText className="w-8 h-8 text-slate-400" />}
            title={(items ?? []).length === 0 ? "Henüz fatura yok" : "Bu tarih aralığında fatura bulunamadı"}
            hint={(items ?? []).length === 0
              ? "Sağ üstteki 'Yeni Fatura' butonu ile başlayın."
              : "Tarih filtresini genişletin veya temizleyin."}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr className="text-left">
                  <th className="px-5 py-3 font-semibold">Tarih</th>
                  <th className="px-5 py-3 font-semibold">Fatura No</th>
                  <th className="px-5 py-3 font-semibold">
                    {type === "sale" ? "Müşteri" : "Tedarikçi"}
                  </th>
                  <th className="px-5 py-3 font-semibold text-right">KDV Hariç</th>
                  <th className="px-5 py-3 font-semibold text-right">KDV</th>
                  <th className="px-5 py-3 font-semibold text-right">Toplam</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(filteredItems ?? []).map((it) => {
                  const partyId = type === "sale" ? it.customer_id : it.vendor_id;
                  const partyList = type === "sale" ? customers : vendors;
                  const party = partyList.find((x) => x.id === partyId);
                  return (
                    <tr key={it.id} data-testid={`invoice-row-${it.id}`} className="hover:bg-slate-50/60">
                      <td className="px-5 py-3">{fmtDate(it.invoice_date)}</td>
                      <td className="px-5 py-3 text-slate-500">
                        <button
                          type="button"
                          onClick={() => { setEditing(it); setOpen(true); }}
                          className="text-left text-slate-800 hover:text-slate-900 hover:underline cursor-pointer font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2 rounded"
                          data-testid={`invoice-title-${it.id}`}
                          title="Faturayı düzenle"
                        >
                          {it.invoice_number || "—"}
                        </button>
                      </td>
                      <td className="px-5 py-3">{party?.name || "—"}</td>
                      <td className="px-5 py-3 text-right">{fmtTRY(it.subtotal_ex_vat)}</td>
                      <td className="px-5 py-3 text-right">{fmtTRY(it.total_vat)}</td>
                      <td className="px-5 py-3 text-right font-semibold">{fmtTRY(it.grand_total)}</td>
                      <td className="px-5 py-3 text-right whitespace-nowrap">
                        <Button size="icon" variant="ghost" className="h-7 w-7"
                          onClick={() => { setEditing(it); setOpen(true); }}
                          data-testid={`invoice-edit-${it.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:bg-red-50"
                          onClick={() => onDelete(it.id)}
                          data-testid={`invoice-delete-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <InvoiceFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
        type={type}
        customers={customers}
        vendors={vendors}
        jobs={jobs}
        purchases={purchases}
        vatRates={vatRates}
        onSaved={() => { load(); setOpen(false); setEditing(null); }}
      />
    </div>
  );
}

function TabPill({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

// ------------------------------------------------------------------
// Invoice form dialog — kalem tablosu + otomatik toplam hesaplama.
// vat_rate default seçim aktif oranların ``is_default`` olanı; kullanıcı
// satır bazında değiştirebilir.  Toplamlar her kalem değişiminde
// client-side önizleme olarak hesaplanır, POST/PUT sonrası sunucudan
// gelen canonical değerlerle güncellenir.
// ------------------------------------------------------------------
function InvoiceFormDialog({
  open, onOpenChange, editing, type, customers, vendors, jobs, purchases, vatRates, onSaved,
}) {
  // Mobil kart/sticky düzeni yalnızca Yeni Satış Faturası'nda (Yeni İş formuyla aynı).
  const saleCreate = type === "sale" && !editing;
  const [form, setForm] = useState({
    invoice_date: todayISO(),
    invoice_number: "",
    customer_id: "",
    vendor_id: "",
    job_id: "",
    purchase_id: "",
    description: "",
    items: [makeEmptyInvoiceItem(vatRates)],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        invoice_date: editing.invoice_date || todayISO(),
        invoice_number: editing.invoice_number || "",
        customer_id: editing.customer_id || "",
        vendor_id: editing.vendor_id || "",
        job_id: editing.job_id || "",
        purchase_id: editing.purchase_id || "",
        description: editing.description || "",
        items: (editing.items || []).map((it) => ({
          description: it.description || "",
          quantity: String(it.quantity ?? 1),
          unit_price: String(it.unit_price ?? 0),
          vat_rate: String(it.vat_rate ?? 20),
        })),
      });
    } else {
      setForm({
        invoice_date: todayISO(),
        invoice_number: "",
        customer_id: "", vendor_id: "", job_id: "", purchase_id: "",
        description: "",
        items: [makeEmptyInvoiceItem(vatRates)],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, vatRates]);

  const relatedJobs = useMemo(() => {
    if (type !== "sale") return [];
    return jobs ?? [];
  }, [type, jobs]);
  const relatedPurchases = useMemo(() => {
    if (type !== "purchase") return [];
    return purchases ?? [];
  }, [type, purchases]);

  // Satış: iş seçimi ZORUNLU; iş seçilince customer_id işten türer.
  // Alış: satın alma ZORUNLU; satın alma seçilince vendor_id ondan türer.
  const setJob = (jobId) => {
    const job = (jobs ?? []).find((j) => j.id === jobId);
    setForm((f) => ({
      ...f,
      job_id: jobId,
      customer_id: job?.customer_id || "",
    }));
  };
  const setPurchase = (purId) => {
    const pur = (purchases ?? []).find((p) => p.id === purId);
    setForm((f) => ({
      ...f,
      purchase_id: purId,
      vendor_id: pur?.vendor_id || "",
    }));
  };

  const setItems = (nextItems) => setForm((f) => ({ ...f, items: nextItems }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (type === "sale") {
      if (!form.job_id) { toast.error("Satış faturası için iş seçin"); return; }
      if (!form.customer_id) { toast.error("Seçilen işin müşterisi bulunamadı"); return; }
    } else {
      if (!form.purchase_id) { toast.error("Alış faturası için satın alma seçin"); return; }
      if (!form.vendor_id) { toast.error("Seçilen satın almanın tedarikçisi bulunamadı"); return; }
    }
    if (!form.items.length) { toast.error("En az bir kalem eklemelisiniz"); return; }
    const payload = {
      type,
      customer_id:  type === "sale" ? form.customer_id : null,
      vendor_id:    type === "purchase" ? form.vendor_id : null,
      job_id:       type === "sale" ? form.job_id : null,
      purchase_id:  type === "purchase" ? form.purchase_id : null,
      invoice_date: form.invoice_date,
      invoice_number: (form.invoice_number || "").trim(),
      description:  (form.description || "").trim(),
      items: serializeInvoiceItems(form.items),
    };
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/invoices/${editing.id}`, payload);
        toast.success("Fatura güncellendi");
      } else {
        await api.post("/invoices", payload);
        toast.success("Fatura oluşturuldu");
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-4xl max-h-[92vh] overflow-y-auto ${saleCreate ? "max-md:max-h-[calc(100dvh_-_1rem)] max-md:overflow-x-hidden" : ""}`}
        data-testid="invoice-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Fatura Düzenle" : (type === "sale" ? "Yeni Satış Faturası" : "Yeni Alış Faturası")}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={onSubmit} className={`space-y-5 ${saleCreate ? "min-w-0" : ""}`}>
          {/* Fatura bilgileri */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 ${saleCreate ? "[&>*]:min-w-0" : ""}`}>
            <div className="space-y-1.5">
              <Label className="text-xs">Fatura Tarihi *</Label>
              <Input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                required
                className="h-10 rounded-lg"
                data-testid="invoice-date-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Fatura No</Label>
              <Input
                value={form.invoice_number}
                onChange={(e) => setForm({ ...form, invoice_number: e.target.value })}
                placeholder="Opsiyonel"
                className="h-10 rounded-lg"
                data-testid="invoice-number-input"
              />
            </div>

            {type === "sale" ? (
              <div className="space-y-1.5">
                <Label className="text-xs">İlgili İş *</Label>
                <Select
                  value={form.job_id}
                  onValueChange={setJob}
                >
                  <SelectTrigger className="h-10 rounded-lg" data-testid="invoice-job-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {relatedJobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.name} — {j.customer_name || (customers.find((c) => c.id === j.customer_id) || {}).name || "?"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label className="text-xs">İlgili Satın Alma *</Label>
                <Select
                  value={form.purchase_id}
                  onValueChange={setPurchase}
                >
                  <SelectTrigger className="h-10 rounded-lg" data-testid="invoice-purchase-select">
                    <SelectValue placeholder="Seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {relatedPurchases.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {(p.description || fmtDate(p.date))} — {p.vendor_name || (vendors.find((v) => v.id === p.vendor_id) || {}).name || "?"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {type === "sale" ? (
              <div className="space-y-1.5" data-testid="invoice-customer-readout">
                <Label className="text-xs">Müşteri</Label>
                <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700">
                  {form.customer_id
                    ? (customers.find((c) => c.id === form.customer_id) || {}).name || "—"
                    : <span className="text-slate-400">İş seçildiğinde otomatik gelir</span>}
                </div>
              </div>
            ) : (
              <div className="space-y-1.5" data-testid="invoice-vendor-readout">
                <Label className="text-xs">Tedarikçi</Label>
                <div className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-3 flex items-center text-sm text-slate-700">
                  {form.vendor_id
                    ? (vendors.find((v) => v.id === form.vendor_id) || {}).name || "—"
                    : <span className="text-slate-400">Satın alma seçildiğinde otomatik gelir</span>}
                </div>
              </div>
            )}

            <div className="md:col-span-3 space-y-1.5">
              <Label className="text-xs">Açıklama</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="h-10 rounded-lg"
                placeholder="Opsiyonel"
                data-testid="invoice-description-input"
              />
            </div>
          </div>

          {/* Kalemler + toplamlar — reusable component */}
          <InvoiceItemsEditor
            items={form.items}
            setItems={setItems}
            vatRates={vatRates}
            testIdPrefix="invoice-item"
            mobileCards={saleCreate}
          />

          <DialogFooter
            className={saleCreate ? `${MOBILE_STICKY_FOOTER} max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))]` : ""}
            data-testid="invoice-form-footer">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button type="submit" disabled={saving}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="invoice-save-button">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TotalRow({ label, value, bold, testId }) {
  return (
    <div className="flex items-center justify-between" data-testid={testId}>
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-600"}>{label}</span>
      <span className={bold ? "font-semibold text-slate-900" : "text-slate-800"}>{value}</span>
    </div>
  );
}
